<template>
    <div class="team-chat-navbar py-2 ml-1">
        <header class="chat-header px-lg-2 d-flex justify-content-between align-items-start">
            <div class="d-flex align-items-center">
                <!-- Toggle Icon -->
                <div class="sidebar-toggle d-block d-lg-none text-secondary py-25 mr-1">
                    <font-awesome-icon
                        icon="angle-left"
                        size="2x"
                        @click="$emit('update:mq-shall-show-left-sidebar', true)"
                    />
                </div>

                <b-avatar
                    v-if="chat"
                    size="md"
                    class="mr-1 badge-minimal round-large"
                    :variant="chat.picture ? '' : 'info'"
                    :text="resolveInitials(chat.name)"
                    :src="chat.picture"
                    @click.native="
                        triggerActiveChatSidebar({
                            sidebar: 'Contact',
                        })
                    "
                />
                <h6 class="mb-0">
                    <strong class="text-truncate">{{ chat.name }}</strong>
                    <br />

                    <small class="text-secondary">{{ subHeader }}</small>
                </h6>
            </div>

            <div class="d-flex align-items-center action-buttons">
                <b-button
                    v-if="isParticipant && isOwner && activeChat.type === 'group' && !activeChat.isArchived"
                    variant="light"
                    @click="editChat(activeChat.id)"
                >
                    {{ $t("team-chats.edit-group") }}
                </b-button>
                <b-button
                    v-if="isParticipant && isOwner && !activeChat.isArchived"
                    variant="light"
                    @click="deleteChat(activeChat.id)"
                >
                    {{ $t("team-chats.confirm.end-chat") }}
                </b-button>
                <b-button
                    v-if="isParticipant && !isOwner && !activeChat.isArchived && activeChat.type === 'group'"
                    variant="light"
                    @click="leaveChat(activeChat.id, participantId)"
                >
                    {{ $t("team-chats.confirm.leave") }}
                </b-button>
                <b-button
                    v-if="isParticipant && !isOwner && !activeChat.isArchived && activeChat.type === 'one_to_one'"
                    variant="light"
                    @click="deleteChat(activeChat.id)"
                >
                    {{ $t("team-chats.confirm.end-chat") }}
                </b-button>
                <b-button v-if="activeChat.isArchived" variant="light" @click="closeChat()">
                    {{ $t("team-chats.confirm.close") }}
                </b-button>
            </div>
        </header>
    </div>
</template>

<script>
import { BAvatar, BButton } from "bootstrap-vue";
import i18n from "@/libs/i18n";
import { resolveInitials, resolveFirstName } from "@/utils/users";
import { getUserData } from "@/auth/getUserData";
import { computed } from "@vue/composition-api";
import router from "@/router";
import useActiveTeamChat from "../useActiveTeamChat";
import useTeamChat from "../useTeamChat";

export default {
    components: {
        BAvatar,
        BButton,
    },
    props: {
        activeChat: {
            type: Object,
            required: true,
        },
    },
    methods: {
        leaveChat(chatId, participantId) {
            this.$bvModal
                .msgBoxConfirm(this.$t("team-chats.confirm.leave-text"), {
                    title: this.$t("team-chats.confirm.leave-title"),
                    cancelTitle: this.$t("team-chats.confirm.cancel"),
                    cancelVariant: "light",
                    okTitle: this.$t("team-chats.confirm.leave"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (result) {
                        const { leaveTeamChat } = useTeamChat();
                        return leaveTeamChat(chatId, participantId).then(() => {
                            const { leaveTeamChat } = useActiveTeamChat();

                            router.replace({
                                name: "team-chat",
                            });

                            leaveTeamChat();
                        });
                    }
                });
        },
        deleteChat(chatId) {
            this.$bvModal
                .msgBoxConfirm(this.$t("team-chats.confirm.end-text"), {
                    title: this.$t("team-chats.confirm.end-title"),
                    cancelTitle: this.$t("team-chats.confirm.cancel"),
                    cancelVariant: "light",
                    okTitle: this.$t("team-chats.confirm.end-chat"),
                    hideHeaderClose: false,
                    headerCloseVariant: "light",
                    centered: true,
                })
                .then((result) => {
                    if (result) {
                        const { deleteTeamChat } = useTeamChat();

                        return deleteTeamChat(chatId).then(() => {
                            router.replace({
                                name: "team-chat",
                            });
                        });
                    }
                });
        },
        editChat(id) {
            this.$emit("set-edited-chat", id);
        },
        closeChat() {
            router.replace({
                name: "team-chat",
            });
        },
    },
    setup(props) {
        const chat = computed(() => {
            if (props.activeChat.type === "group") {
                return {
                    name: props.activeChat.name,
                };
            }
            const participant = props?.activeChat?.participants?.find(
                (participant) => participant.userId !== getUserData().id
            )?.user;

            return {
                name: participant?.name,
                picture: participant?.pictureUrl,
            };
        });

        const participant = computed(
            () =>
                Array.isArray(props.activeChat.participants) &&
                props.activeChat.participants.find((participant) => participant.userId === getUserData().id)
        );
        const isParticipant = computed(() => Boolean(participant.value));
        const participantId = computed(() => (isParticipant ? participant.value?.id : null));

        const isOwner = computed(() => props.activeChat.userId === getUserData().id);

        const subHeader = computed(() => {
            if (props.activeChat.type === "one_to_one") {
                return props.activeChat.participants.find((participant) => participant.userId !== getUserData().id)
                    ?.user?.email;
            }

            return Array.isArray(props.activeChat.participants)
                ? props.activeChat.participants
                      .filter((participant) => !participant.leftAt)
                      .map((participant) => {
                          if (participant.userId === getUserData().id) {
                              return i18n.t("team-chats.you");
                          } else {
                              return resolveFirstName(participant?.user?.name ?? "Unknown User");
                          }
                      })
                      .filter((x) => x)
                      .join(", ")
                : "";
        });

        return {
            resolveInitials,
            chat,
            subHeader,
            participantId,
            isParticipant,
            isOwner,
        };
    },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-context.scss";
@import "~@/assets/scss/modules/team-chat/activeTeamChat.scss";
</style>
